import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { PaymentMethodModal, defaultMethod, usePaymentMethods } from "@/components/domains/shared";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  ErrorAlert,
  ErrorState,
  Field,
  Input,
  Section,
} from "@/components/ui";
import { domainsApi, longDate, termOptions, type PillTone, type SunriseApplication } from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { ApiError, errorMessage } from "@/lib/http";
import { payableNow } from "@/lib/payments";
import type { PaymentMethod } from "@/lib/types";

const KEY = (orgId: string) => ["domains", orgId, "sunrise-applications"] as const;

/** What the customer reads for an application: "Application submitted" until the registry has decided. */
export function outcome(a: Pick<SunriseApplication, "status" | "refunded">): {
  label: string;
  tone: PillTone;
} {
  switch (a.status) {
    case "allocated":
      return { label: "Allocated: the name is being registered", tone: "success" };
    case "not_allocated":
      return {
        label: a.refunded ? "Not allocated: refunded in full" : "Not allocated: refund in progress",
        tone: "danger",
      };
    case "pending_payment":
      return { label: "Payment being confirmed", tone: "warning" };
    default:
      return { label: "Application submitted", tone: "warning" };
  }
}

/** `/domains/sunrise/apply`: apply for a name in its sunrise period (trademark proof and payment), and follow the applications. */
export function SunriseApplyPage() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [fqdn, setFqdn] = useState(params.get("fqdn") ?? "");
  const [years, setYears] = useState(1);
  const [contactId, setContactId] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const list = useQuery({ queryKey: KEY(orgId), queryFn: () => domainsApi.sunriseApplications(orgId) });
  const contacts = useQuery({
    queryKey: ["domains", orgId, "contacts"],
    queryFn: () => domainsApi.contacts(orgId),
  });
  const methods = usePaymentMethods();
  // A card the person chose not to save isn't listed with the cards on file, so it is kept here (as in the checkout).
  const [oneTime, setOneTime] = useState<PaymentMethod | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const method: PaymentMethod | undefined =
    oneTime ?? defaultMethod((methods.data?.payment_methods ?? []).filter(payableNow));
  const contact = contactId || contacts.data?.contacts[0]?.id || "";

  const quote = useQuery({
    queryKey: ["domains", orgId, "sunrise-quote", fqdn.trim().toLowerCase(), years, contact],
    queryFn: () =>
      domainsApi.sunriseQuote(orgId, { fqdn: fqdn.trim().toLowerCase(), years, contact_id: contact }),
    enabled: fqdn.trim().includes(".") && !!contact,
    retry: false,
  });

  const apply = useMutation({
    mutationFn: () =>
      domainsApi.applySunrise(orgId, {
        fqdn: fqdn.trim().toLowerCase(),
        years,
        contact_id: contact,
        payment_method_id: method?.id ?? null,
        expected_total_cents: quote.data!.total_cents,
        smd: file!,
      }),
    onSuccess: async () => {
      setFile(null);
      await qc.invalidateQueries({ queryKey: KEY(orgId) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.reason === "PRICE_CHANGED") void quote.refetch();
    },
  });
  const needsApproval = apply.error instanceof ApiError && apply.error.reason === "PAYMENT_NEEDS_APPROVAL";

  const reason = !fqdn.trim()
    ? "Enter the name to apply for"
    : !file
      ? "Choose your trademark proof (.smd file)"
      : !contact
        ? "Add a contact for the registration first"
        : !method
          ? "Add a payment method to apply"
          : !quote.data
            ? "Checking the price…"
            : undefined;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Sunrise applications" }]} />
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg">Sunrise applications</h1>
        <p className="text-sm text-fg-subtle">
          During a sunrise period a name can only be applied for, with proof of your trademark. You pay now;
          if the registry doesn't allocate the name to you, you are refunded in full.
        </p>
      </div>

      <Section title="Apply for a name">
        <form
          className="flex flex-col gap-3.5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason) apply.mutate();
          }}
        >
          <Field size="lg" label="Domain name" required>
            {(c) => <Input size="lg" {...c} value={fqdn} onChange={(e) => setFqdn(e.target.value)} />}
          </Field>
          <Field size="lg" label="Years">
            {(c) => (
              <select
                {...c}
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="rounded-lg border border-border bg-surface px-2 py-2 text-sm text-fg"
              >
                {termOptions(undefined, years).map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "year" : "years"}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field size="lg" label="Registrant contact" required>
            {(c) => (
              <select
                {...c}
                value={contact}
                onChange={(e) => setContactId(e.target.value)}
                className="rounded-lg border border-border bg-surface px-2 py-2 text-sm text-fg"
              >
                {(contacts.data?.contacts ?? []).map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.full_name} · {ct.email}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {!contacts.isPending && (contacts.data?.contacts.length ?? 0) === 0 && (
            <p className="text-sm text-fg-subtle">
              You have no contacts yet.{" "}
              <Link to="/domains/contacts" className="font-semibold underline">
                Add one
              </Link>{" "}
              first: the registry needs it for the registration.
            </p>
          )}
          <Field size="lg" label="Payment method" required>
            {() => (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-sm">
                <span className="text-fg">
                  {method ? method.label : "No payment method yet"}
                  {oneTime && method?.id === oneTime.id ? " · this payment only" : ""}
                </span>
                <Button size="sm" variant="secondary" type="button" onClick={() => setPaymentOpen(true)}>
                  {method ? "Change" : "Add a payment method"}
                </Button>
              </div>
            )}
          </Field>
          <Field
            size="lg"
            label="Trademark proof (.smd file)"
            required
            hint="The SMD file from the Trademark Clearinghouse, up to 1 MB."
          >
            {(c) => (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor={c.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border bg-surface px-4 py-4 text-sm text-fg hover:bg-muted focus-within:ring-2 focus-within:ring-brand"
                >
                  <Upload aria-hidden className="size-5 shrink-0 text-fg-subtle" />
                  <span className="min-w-0 flex-1 truncate">
                    {file ? (
                      <span className="font-semibold">{file.name}</span>
                    ) : (
                      <>
                        <span className="font-semibold">Upload SMD file</span>{" "}
                        <span className="text-fg-subtle">· click to choose a .smd file</span>
                      </>
                    )}
                  </span>
                  {file && <span className="shrink-0 text-fg-subtle">Change</span>}
                  <input
                    {...c}
                    type="file"
                    accept=".smd,text/plain"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="sr-only"
                  />
                </label>
              </div>
            )}
          </Field>
          {quote.data && (
            <p className="text-sm font-semibold text-fg">
              Total today: {formatMoney(quote.data.total_cents, "GBP")} incl. VAT ({quote.data.years}{" "}
              {quote.data.years === 1 ? "year" : "years"})
            </p>
          )}
          {quote.isError && <p className="text-sm text-fg-subtle">{errorMessage(quote.error)}</p>}
          {needsApproval ? (
            <Alert tone="danger">
              Your bank needs to approve this card; use a saved card that does not need approval or contact
              support. Nothing was charged.
            </Alert>
          ) : (
            <ErrorAlert error={apply.error} />
          )}
          {apply.isSuccess && (
            <p role="status" className="text-sm font-semibold text-[#007955]">
              Application submitted. We'll email you when the registry decides.
            </p>
          )}
          <div>
            <Button
              variant="brand"
              size="lg"
              type="submit"
              requires="billing:manage"
              loading={apply.isPending}
              disabledReason={reason}
            >
              Pay and apply
            </Button>
          </div>
        </form>
      </Section>

      <Section title="Your applications">
        {list.isPending ? (
          <p className="text-sm text-fg-subtle">Loading…</p>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data.applications.length === 0 ? (
          <p className="text-sm text-fg-subtle">No sunrise applications yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {list.data.applications.map((a) => {
              const o = outcome(a);
              return (
                <li key={a.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold text-fg">{a.fqdn}</span>
                    <span className="text-xs text-fg-subtle">
                      {a.years} {a.years === 1 ? "year" : "years"} · {formatMoney(a.total_cents, "GBP")} incl.
                      VAT · applied {longDate(a.created_at)}
                    </span>
                  </div>
                  <Badge tone={o.tone} size="sm">
                    {o.label}
                  </Badge>
                  {a.order_id && (
                    <Link
                      to={`/domains/orders/${a.order_id}`}
                      className="text-sm font-semibold text-brand-500 hover:underline"
                    >
                      View order →
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      <PaymentMethodModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        selectedId={method?.id ?? null}
        title={method ? "Change payment method" : "Add a payment method"}
        oneTime
        onChosen={(m) => {
          setOneTime(m.saved ? null : m);
          setPaymentOpen(false);
        }}
      />
    </div>
  );
}
